<script>
import { getLastUsedLabels } from '@/utils/lastUsedLabels';
import { TEXT_LINE_LABEL, syntheticTextLineLabelOption, BLACKOUT_LABEL, syntheticBlackoutLabelOption } from '@/utils/textLabels';
import { filterAndRankLabelsForSearch } from '@/utils/labelSearchRank';

export default {
  name: "LoadSearchLabelsMixin",
  data() {
    return {
      labelSearchMinChars: 1,
      search_label_options: [],
      search_label_loading: false,
      search_label_selected: null,
      search_label_value: null,
      labelSearchInput: '',
      labelSearchSeq: 0,
      labelSearchDebounceTimer: null,
    };
  },
  beforeUnmount() {
    if (this.labelSearchDebounceTimer) {
      clearTimeout(this.labelSearchDebounceTimer);
    }
  },
  methods: {
    labelsCatalogReady() {
      const projectId =
        typeof sessionStorage !== 'undefined'
          ? sessionStorage.getItem('project_id')
          : null
      if (!projectId || !this.$store) {
        return false
      }
      const loaded = this.$store.getters['label/get_loaded'] === true
      const loadedPid = this.$store.getters['label/get_loaded_for_project_id']
      const pidMatch = String(loadedPid) === String(projectId)
      return loaded && pidMatch
    },
    normalizedLabelTypeFilter() {
      let ft = this.label_type
      if (ft === 'box' || ft === 1 || ft === '1') {
        return 1
      }
      if (ft === 'point' || ft === 2 || ft === '2') {
        return 2
      }
      if (ft === null || ft === undefined || ft === '') {
        return null
      }
      const n = Number(ft)
      return Number.isNaN(n) ? null : n
    },
    filterStoreLabelsForSearch(query) {
      const limit =
        this.search_label_limit != null ? this.search_label_limit : 50
      const ft = this.normalizedLabelTypeFilter()
      const items = this.$store.state.label.items || []
      return filterAndRankLabelsForSearch(items, query, { limit, type: ft })
    },
    mapStoreLabelsToOptions(rows) {
      return (rows || []).map((label) => ({
        id: label.id,
        name: label.name,
        color: label.color,
        type: label.type,
      }))
    },
    projectHasTextLineLabel() {
      if (!this.labelsCatalogReady()) {
        return false
      }
      return (this.$store.state.label.items || []).some(
        (l) => l && l.name && String(l.name).toLowerCase() === TEXT_LINE_LABEL,
      )
    },
    queryMatchesTextLine(query) {
      const q = String(query || '').trim().toLowerCase()
      if (!q) {
        return true
      }
      return TEXT_LINE_LABEL.includes(q) || q.includes('text')
    },
    withCreateLabelOption(options, query) {
      if (!this.allow_create_label) {
        return options || [];
      }
      const q = String(query || '').trim();
      if (q.length < 2) {
        return options || [];
      }
      const rows = options || [];
      if (rows.some((o) => o && !o.disabled && String(o.name).toLowerCase() === q.toLowerCase())) {
        return rows;
      }
      if (rows.some((o) => o && o.id === '__create_label__' && o.createName === q)) {
        return rows;
      }
      return [
        {
          id: '__create_label__',
          name: this.$t('annotate.create_label_option', { name: q }),
          createName: q,
          color: '0,99,133',
          type: this.create_label_type != null ? this.create_label_type : 1,
        },
        ...rows,
      ];
    },
    applyLabelSearchOptions(options, query) {
      return this.withCreateLabelOption(
        this.withEnsureBlackoutOption(
          this.withEnsureTextLineOption(options || [], query),
          query,
        ),
        query,
      );
    },
    withEnsureTextLineOption(options, query) {
      if (!this.ensure_text_line_label || this.normalizedLabelTypeFilter() !== 1) {
        return options || []
      }
      if (this.projectHasTextLineLabel()) {
        return options || []
      }
      const rows = options || []
      if (rows.some((o) => o && o.name && String(o.name).toLowerCase() === TEXT_LINE_LABEL)) {
        return rows
      }
      if (!this.queryMatchesTextLine(query)) {
        return rows
      }
      return [syntheticTextLineLabelOption(), ...rows]
    },
    projectHasBlackoutLabel() {
      if (!this.labelsCatalogReady()) {
        return false
      }
      return (this.$store.state.label.items || []).some(
        (l) => l && l.name && String(l.name).toLowerCase() === BLACKOUT_LABEL.toLowerCase(),
      )
    },
    queryMatchesBlackout(query) {
      const q = String(query || '').trim().toLowerCase()
      if (!q) {
        return true
      }
      return BLACKOUT_LABEL.toLowerCase().includes(q) || q.includes('black')
    },
    withEnsureBlackoutOption(options, query) {
      if (!this.ensure_blackout_label || this.normalizedLabelTypeFilter() !== 1) {
        return options || []
      }
      if (this.projectHasBlackoutLabel()) {
        return options || []
      }
      const rows = options || []
      if (rows.some((o) => o && o.name && String(o.name).toLowerCase() === BLACKOUT_LABEL.toLowerCase())) {
        return rows
      }
      if (!this.queryMatchesBlackout(query)) {
        return rows
      }
      return [syntheticBlackoutLabelOption(), ...rows]
    },
    buildLastUsedSearchOptions(queryTrimmed) {
      if (this.track_last_used === false) {
        return [];
      }
      const projectId = typeof sessionStorage !== 'undefined'
        ? sessionStorage.getItem('project_id')
        : null;
      let rows = getLastUsedLabels(projectId, this.label_type);
      if (queryTrimmed && queryTrimmed.length === 1) {
        const q = queryTrimmed.toLowerCase();
        rows = rows.filter((r) => r && r.name && String(r.name).toLowerCase().includes(q));
      }
      if (!rows.length) {
        return [];
      }
      const header = {
        id: '__box21_last_used_header__',
        name: this.$t('filter_tree.label_search_last_used_header'),
        disabled: true,
      };
      return [header, ...rows.map((r) => ({
        id: r.id,
        name: r.name,
        color: r.color,
        type: r.type,
      }))];
    },
    search_label_value_updated(value) {
      this.labelSearchInput = value == null ? '' : String(value);
      const trimmed = this.labelSearchInput.trim();
      if (this.labelSearchDebounceTimer) {
        clearTimeout(this.labelSearchDebounceTimer);
      }
      if (!trimmed) {
        this.labelSearchSeq += 1;
        this.search_label_loading = false;
        if (this.clear_on_select) {
          this.search_label_options = this.applyLabelSearchOptions(
            this.buildLastUsedSearchOptions(''),
            '',
          );
        } else if (this.labelsCatalogReady()) {
          // Show full local catalog so the menu is usable without typing (small projects).
          this.search_label_options = this.applyLabelSearchOptions(
            this.mapStoreLabelsToOptions(this.filterStoreLabelsForSearch('')),
            '',
          );
        } else if (this.search_label_selected) {
          this.search_label_options = [this.search_label_selected];
        } else {
          this.search_label_options = [];
        }
        return;
      }
      if (this.search_label_selected && this.search_label_selected.name === value) {
        this.search_label_loading = false;
        return;
      }
      this.search_label_loading = true;
      this.labelSearchDebounceTimer = setTimeout(() => {
        this.run_label_search(value);
      }, 300);
    },
    async run_label_search(value) {
      if (this.search_label_selected && this.search_label_selected.name === value) {
        this.search_label_loading = false;
        return;
      }
      const seq = ++this.labelSearchSeq;
      const useLocal = this.labelsCatalogReady();
      if (!useLocal) {
        this.search_label_loading = true;
      } else {
        this.search_label_loading = false;
      }
      try {
        let rows = [];
        if (useLocal) {
          rows = this.filterStoreLabelsForSearch(value);
        } else {
          const limit =
            this.search_label_limit != null ? this.search_label_limit : 50;
          rows =
            (await this.$api.label.labelSearch(value, {
              type: this.label_type,
              limit,
            })) || [];
        }
        if (seq !== this.labelSearchSeq) {
          return;
        }
        this.search_label_options = this.applyLabelSearchOptions(
          this.mapStoreLabelsToOptions(rows),
          value,
        );
      } catch (e) {
        if (seq !== this.labelSearchSeq) {
          return;
        }
        this.search_label_options = this.applyLabelSearchOptions([], value);
      } finally {
        if (seq === this.labelSearchSeq) {
          this.search_label_loading = false;
        }
      }
    },
  },
};
</script>

<style scoped>
</style>
